import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Truck, Plus, ChevronDown, ChevronRight, PackageCheck, ArrowRightLeft, Factory, ShoppingCart, ExternalLink, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const num = (n) => (n || 0).toLocaleString("id-ID");
const fmtDate = (s) => { try { return new Date(s).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s || "-"; } };

function SourceBadge({ r }) {
  const navigate = useNavigate();
  const type = r.ref_type || r.source || "Manual";
  const isProd = type === "Produksi";
  const isPO = type === "PO";
  const cls = isProd ? "bg-violet-100 text-violet-700" : isPO ? "bg-blue-100 text-blue-700" : "bg-taupe/20 text-cocoa";
  const label = isProd ? "Produksi" : isPO ? "Purchase" : "Manual";
  const go = (e) => { e.stopPropagation(); if (isProd) navigate("/production/kanban"); else if (isPO) navigate("/purchase-orders"); };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>
      {(r.ref_number || r.ref) && <button onClick={go} className="text-[11px] text-cocoa/70 hover:text-cocoa inline-flex items-center gap-0.5 font-mono" data-testid={`ppo-ref-${r.id}`}>{r.ref_number || r.ref}{(isProd || isPO) && <ExternalLink className="w-3 h-3" />}</button>}
    </span>
  );
}

export default function PurchasePO() {
  const { can } = useAuth();
  const write = can("warehouse_admin", "sales");
  const [tab, setTab] = useState("kedatangan");

  return (
    <div data-testid="purchase-po-page">
      <PageHeader title="Purchase PO" subtitle="Pusat kedatangan pembelian PO (Produksi/Garment & Supplier) — barang datang, pemenuhan, dan Stok PO siap jual" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="kedatangan" data-testid="ppo-tab-kedatangan"><Truck className="w-4 h-4 mr-1.5" />Kedatangan</TabsTrigger>
          <TabsTrigger value="stok" data-testid="ppo-tab-stok"><PackageCheck className="w-4 h-4 mr-1.5" />Stok PO Siap Jual</TabsTrigger>
          <TabsTrigger value="ringkasan" data-testid="ppo-tab-ringkasan"><LayoutGrid className="w-4 h-4 mr-1.5" />Ringkasan Alokasi</TabsTrigger>
        </TabsList>
        <TabsContent value="kedatangan"><ArrivalsTab write={write} /></TabsContent>
        <TabsContent value="stok"><ReadyStockTab write={write} /></TabsContent>
        <TabsContent value="ringkasan"><AllocationSummaryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ArrivalsTab({ write }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [addOpen, setAddOpen] = useState(false);

  const load = () => api.get("/preorder-batches/arrivals", { params: { q: q || undefined } })
    .then((r) => setRows(r.data.items || [])).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / batch / ref..." className="pl-9 bg-white" data-testid="ppo-search" />
        </div>
        {write && <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white ml-auto" data-testid="ppo-add-btn"><Plus className="w-4 h-4 mr-1.5" />Catat Kedatangan</Button>}
      </div>
      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8"></TableHead>
              <TableHead>Tanggal</TableHead><TableHead>Batch</TableHead><TableHead>Sumber</TableHead>
              <TableHead className="text-right">Datang</TableHead><TableHead className="text-right">Siap Kirim</TableHead><TableHead className="text-right">Stok PO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <TableRow className="cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))} data-testid={`ppo-row-${r.id}`}>
                  <TableCell>{expanded[r.id] ? <ChevronDown className="w-4 h-4 text-wmuted" /> : <ChevronRight className="w-4 h-4 text-wmuted" />}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.date || r.created_at)}</TableCell>
                  <TableCell className="text-sm"><span className="font-mono text-cocoa">{r.batch_code}</span><div className="text-xs text-wmuted">{r.batch_name}</div></TableCell>
                  <TableCell><SourceBadge r={r} />{r.auto && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold whitespace-nowrap" data-testid={`ppo-auto-${r.id}`}>Otomatis (Produksi)</span>}</TableCell>
                  <TableCell className="text-right font-semibold">{num(r.total_arrived)}</TableCell>
                  <TableCell className="text-right text-emerald-600">{num(r.total_allocated)}</TableCell>
                  <TableCell className="text-right text-amber-600">{num(r.total_leftover)}</TableCell>
                </TableRow>
                {expanded[r.id] && (
                  <TableRow className="hover:bg-transparent bg-cocoa/[0.03]">
                    <TableCell></TableCell>
                    <TableCell colSpan={6} className="py-2">
                      <Table>
                        <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-right">Datang</TableHead><TableHead className="text-right">Ke Keep</TableHead><TableHead className="text-right">Stok PO</TableHead></TableRow></TableHeader>
                        <TableBody>{(r.result || []).map((it, i) => (
                          <TableRow key={i} className="hover:bg-transparent"><TableCell className="text-sm">{it.product_name}</TableCell><TableCell className="text-sm">{it.color}/{it.size}</TableCell><TableCell className="text-right">{num(it.arrived)}</TableCell><TableCell className="text-right text-emerald-600">{num(it.allocated_to_keep)}</TableCell><TableCell className="text-right text-amber-600">{num(it.leftover)}</TableCell></TableRow>
                        ))}</TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10"><PackageCheck className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada kedatangan PO.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
      {addOpen && <AddArrivalDialog onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

function AddArrivalDialog({ onClose, onDone }) {
  const [source, setSource] = useState("Produksi");
  const [refId, setRefId] = useState("");
  const [refNumber, setRefNumber] = useState("");
  const [refOptions, setRefOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [variants, setVariants] = useState([]);
  const [qtys, setQtys] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/preorder-batches/po-products").then((r) => setProducts(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    setRefId(""); setRefNumber(""); setRefOptions([]);
    if (source === "Produksi") api.get("/production/orders").then((r) => setRefOptions((r.data || []).map((o) => ({ id: o.id, number: o.number, label: `${o.number} — ${o.product_name || ""}${o.partner_name ? " · " + o.partner_name : ""}` })))).catch(() => {});
    else if (source === "PO") api.get("/purchase-orders").then((r) => setRefOptions((r.data || []).map((o) => ({ id: o.id, number: o.number, label: `${o.number} — ${o.supplier_name || ""}` })))).catch(() => {});
  }, [source]);
  useEffect(() => {
    setVariants([]); setQtys({});
    if (!productId) return;
    api.get("/preorder-batches/variant-quotas", { params: { product_id: productId } }).then((r) => setVariants(r.data.items || [])).catch(() => {});
  }, [productId]);

  const pickRef = (id) => { setRefId(id); setRefNumber((refOptions.find((o) => o.id === id) || {}).number || ""); };
  const setQty = (qid, v) => setQtys((s) => ({ ...s, [qid]: v }));
  const totalQty = Object.values(qtys).reduce((a, v) => a + (parseInt(v) || 0), 0);

  const submit = async () => {
    if (source !== "Manual" && !refId) return toast.error(`Pilih ${source === "Produksi" ? "order produksi" : "Purchase Order"} sebagai referensi`);
    const lines = Object.entries(qtys).filter(([, v]) => parseInt(v) > 0).map(([po_quota_id, v]) => ({ po_quota_id, qty: parseInt(v) }));
    if (!lines.length) return toast.error("Isi minimal satu qty kedatangan ke salah satu batch");
    setBusy(true);
    try {
      const r = await api.post("/preorder-batches/arrivals/allocate", { source, ref_type: source, ref_id: refId || null, ref_number: refNumber, ref: refNumber, lines });
      toast.success(`Dialokasikan ke ${r.data.batches} batch · ${r.data.allocated} siap kirim, ${r.data.leftover} jadi Stok PO`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ppo-add-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Catat Kedatangan Purchase PO</DialogTitle><DialogDescription>Pilih sumber & produk, lalu bagi barang datang tiap varian ke satu/beberapa batch Open.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Jenis Sumber</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="bg-white" data-testid="ppo-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Produksi"><span className="inline-flex items-center gap-1.5"><Factory className="w-3.5 h-3.5" />Produksi (Garment)</span></SelectItem>
                  <SelectItem value="PO"><span className="inline-flex items-center gap-1.5"><ShoppingCart className="w-3.5 h-3.5" />Purchase (Supplier)</span></SelectItem>
                  <SelectItem value="Manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Produk</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="bg-white" data-testid="ppo-product"><SelectValue placeholder="Pilih produk PO" /></SelectTrigger>
                <SelectContent>{products.length ? products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>) : <div className="px-3 py-2 text-xs text-wmuted">Tidak ada produk dengan batch Open</div>}</SelectContent>
              </Select>
            </div>
          </div>
          {source !== "Manual" && (
            <div><Label>{source === "Produksi" ? "Order Produksi (Garment)" : "Purchase Order (Supplier)"}</Label>
              <Select value={refId} onValueChange={pickRef}>
                <SelectTrigger className="bg-white" data-testid="ppo-ref"><SelectValue placeholder={`Pilih ${source === "Produksi" ? "order produksi" : "PO"}`} /></SelectTrigger>
                <SelectContent>{refOptions.length ? refOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>) : <div className="px-3 py-2 text-xs text-wmuted">Tidak ada data</div>}</SelectContent>
              </Select>
            </div>
          )}
          {productId && (
            <div className="space-y-2" data-testid="ppo-variant-map">
              <div className="text-xs font-semibold text-cocoa uppercase tracking-wide">Pemetaan ke Batch (per varian)</div>
              {variants.length ? variants.map((v) => (
                <div key={v.variant_id} className="border border-taupe/20 rounded-lg p-2.5">
                  <div className="text-sm font-medium text-cocoa mb-1.5">{v.color}/{v.size}</div>
                  <div className="space-y-1.5">
                    {v.batches.map((b) => (
                      <div key={b.po_quota_id} className="grid grid-cols-[1fr_auto] gap-2 items-center">
                        <div className="text-xs">
                          <span className="font-mono text-cocoa">{b.batch_code}</span> <span className="text-wmuted">{b.batch_name}</span>
                          <span className="text-wmuted"> · reserved {b.reserved} · sisa {b.remaining} · stok PO {b.ready}</span>
                        </div>
                        <Input type="number" min={0} value={qtys[b.po_quota_id] || ""} onChange={(e) => setQty(b.po_quota_id, e.target.value)} className="h-8 w-24 text-center" placeholder="0" data-testid={`ppo-qty-${b.po_quota_id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )) : <div className="text-center text-wmuted py-6 text-sm">Produk ini belum punya kuota di batch Open.</div>}
            </div>
          )}
          <p className="text-xs text-wmuted">Barang datang tiap batch dialokasikan FIFO ke keep Pre-Order (jadi "Siap Kirim"); sisanya jadi Stok PO siap jual. Untuk memindah Stok PO antar batch setelah masuk, gunakan tab "Stok PO Siap Jual → Alihkan → Oper ke batch lain".</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} disabled={busy || totalQty <= 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ppo-submit"><Truck className="w-4 h-4 mr-1.5" />{busy ? "Memproses..." : `Catat Kedatangan (${totalQty})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReadyStockTab({ write }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [route, setRoute] = useState(null);
  const load = () => api.get("/preorder-batches/ready-stock", { params: { q: q || undefined } })
    .then((r) => setRows(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  return (
    <div>
      <div className="relative flex-1 max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / batch..." className="pl-9 bg-white" data-testid="ppo-stok-search" />
      </div>
      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Batch</TableHead><TableHead>Produk</TableHead><TableHead>Varian</TableHead>
              <TableHead className="text-right">Siap Jual</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`ppo-stok-row-${r.id}`}>
                <TableCell className="text-sm"><span className="font-mono text-cocoa">{r.batch_code}</span><div className="text-xs text-wmuted">{r.batch_name}</div></TableCell>
                <TableCell className="font-medium text-cocoa">{r.product_name}{r.keep_as_po && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-cocoa/10 text-cocoa font-semibold align-middle" data-testid={`ppo-tetappo-${r.id}`}>Tetap PO</span>}</TableCell>
                <TableCell className="text-sm">{r.color}/{r.size}</TableCell>
                <TableCell className="text-right font-bold text-status-passed">{num(r.ready_qty)}</TableCell>
                <TableCell className="text-right">
                  {write && <Button size="sm" onClick={() => setRoute(r)} className="bg-cocoa hover:bg-cocoa-dark text-ivory h-8" data-testid={`ppo-route-${r.id}`}><ArrowRightLeft className="w-4 h-4 mr-1.5" />Alihkan</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-10"><PackageCheck className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada stok PO siap jual.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
      {route && <RouteDialog line={route} onClose={() => setRoute(null)} onDone={() => { setRoute(null); load(); }} />}
    </div>
  );
}

function AllocationSummaryTab() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [variants, setVariants] = useState([]);

  useEffect(() => { api.get("/preorder-batches/po-products").then((r) => { const its = r.data.items || []; setProducts(its); if (its[0]) setProductId(its[0].id); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!productId) { setVariants([]); return; }
    api.get("/preorder-batches/variant-quotas", { params: { product_id: productId } }).then((r) => setVariants(r.data.items || [])).catch(() => {});
  }, [productId]);

  const batchCols = [];
  const seen = {};
  variants.forEach((v) => v.batches.forEach((b) => { if (!seen[b.batch_id]) { seen[b.batch_id] = true; batchCols.push({ batch_id: b.batch_id, batch_code: b.batch_code, batch_name: b.batch_name }); } }));
  batchCols.sort((a, b) => (a.batch_code || "").localeCompare(b.batch_code || ""));
  const cell = (v, bid) => v.batches.find((b) => b.batch_id === bid);
  const colTotal = (bid) => variants.reduce((a, v) => a + (cell(v, bid)?.arrived || 0), 0);
  const rowTotal = (v) => v.batches.reduce((a, b) => a + (b.arrived || 0), 0);
  const grand = variants.reduce((a, v) => a + rowTotal(v), 0);

  return (
    <div data-testid="ppo-alloc-summary">
      <div className="relative max-w-sm mb-4">
        <Select value={productId} onValueChange={setProductId}>
          <SelectTrigger className="bg-white" data-testid="ppo-sum-product"><SelectValue placeholder="Pilih produk PO" /></SelectTrigger>
          <SelectContent>{products.length ? products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>) : <div className="px-3 py-2 text-xs text-wmuted">Tidak ada produk dengan batch Open</div>}</SelectContent>
        </Select>
      </div>
      <Panel>
        {variants.length && batchCols.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="sticky left-0 bg-ivory">Varian</TableHead>
                  {batchCols.map((c) => <TableHead key={c.batch_id} className="text-right whitespace-nowrap"><span className="font-mono">{c.batch_code}</span></TableHead>)}
                  <TableHead className="text-right font-bold">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variants.map((v) => (
                  <TableRow key={v.variant_id} data-testid={`ppo-sum-row-${v.variant_id}`}>
                    <TableCell className="font-medium text-cocoa sticky left-0 bg-white">{v.color}/{v.size}</TableCell>
                    {batchCols.map((c) => { const b = cell(v, c.batch_id); return <TableCell key={c.batch_id} className="text-right">{b ? <span className={b.arrived > 0 ? "font-semibold text-cocoa" : "text-wmuted"}>{b.arrived}<span className="text-[10px] text-wmuted">/{b.planned}</span></span> : <span className="text-wmuted/40">—</span>}</TableCell>; })}
                    <TableCell className="text-right font-bold text-cocoa">{rowTotal(v)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="hover:bg-transparent bg-cocoa/5 font-semibold text-cocoa">
                  <TableCell className="sticky left-0 bg-cocoa/5">Total dipetakan</TableCell>
                  {batchCols.map((c) => <TableCell key={c.batch_id} className="text-right">{colTotal(c.batch_id)}</TableCell>)}
                  <TableCell className="text-right" data-testid="ppo-sum-grand">{grand}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : <div className="text-center text-wmuted py-10"><LayoutGrid className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada kuota di batch Open untuk produk ini.</div>}
      </Panel>
      <p className="text-[11px] text-wmuted mt-2">Angka = pcs sudah dipetakan (arrived) / planned tiap batch. Kolom = batch Open, baris = varian.</p>
    </div>
  );
}

function RouteDialog({ line, onClose, onDone }) {
  const [action, setAction] = useState(line.keep_as_po ? "keep" : "regular");
  const [qty, setQty] = useState(line.ready_qty);
  const [batches, setBatches] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/preorder-batches", { params: { status: "Open" } }).then((r) => setBatches((r.data.items || []).filter((b) => b.id !== line.batch_id))).catch(() => {}); }, [line.batch_id]);

  const submit = async () => {
    const n = parseInt(qty) || 0;
    if (action !== "keep" && (n <= 0 || n > line.ready_qty)) return toast.error(`Qty 1–${line.ready_qty}`);
    if (action === "transfer" && !target) return toast.error("Pilih batch tujuan");
    setBusy(true);
    try {
      await api.post(`/preorder-batches/lines/${line.id}/route`, { action, qty: n, target_batch_id: action === "transfer" ? target : undefined });
      toast.success("Stok PO dialihkan"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ppo-route-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Alihkan Stok PO</DialogTitle><DialogDescription>Pindahkan stok PO siap jual: biarkan sebagai PO, ke stok reguler, atau oper ke batch lain.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-wmuted">{line.product_name} {line.color}/{line.size} · siap jual <b className="text-status-passed">{num(line.ready_qty)}</b>{line.keep_as_po && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-cocoa/10 text-cocoa font-semibold">Tetap PO</span>}</div>
          <div><Label>Tujuan</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="bg-white" data-testid="ppo-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="keep">Biarkan sebagai PO siap jual</SelectItem>
                {!line.keep_as_po && <SelectItem value="regular">Pindah ke stok reguler (gudang HOME)</SelectItem>}
                <SelectItem value="transfer">Oper ke batch lain</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {action === "transfer" && (
            <div><Label>Batch Tujuan</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="bg-white" data-testid="ppo-target"><SelectValue placeholder="Pilih batch" /></SelectTrigger>
                <SelectContent>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {action !== "keep" && <div><Label>Qty</Label><Input type="number" min={1} max={line.ready_qty} value={qty} onChange={(e) => setQty(e.target.value)} className="bg-white" data-testid="ppo-route-qty" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ppo-route-submit">{busy ? "Memproses..." : "Alihkan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
